import {
	type CustomDomain as CustomDomainMessage,
	CustomDomain_CertificateStatus,
	CustomDomain_Status,
} from "../gen/kitecorp/transferify/storefront/v1/custom_domain_pb";
import type {
	SubdomainClaim as SubdomainClaimMessage,
} from "../gen/kitecorp/transferify/storefront/v1/subdomain_claim_pb";
import {instantText} from "./accountMessages";
import type {SubdomainClaim} from "./AccountSubdomainApi";
import type {CdnCertificateStatus, CustomDomain, CustomDomainStatus} from "./customDomain/customDomain";

/**
 * The custom domain and subdomain claim answers of the storefront services (kitecorp/transferify-ui#3956), mapped back
 * onto the REST-shaped {@link CustomDomain} and {@link SubdomainClaim} the sections read. Pure functions of one message
 * each; a rejected call never reaches them.
 *
 * An optional field the message does not carry is the `null` the REST body printed for it, and an instant is the
 * whole-second text the REST body printed (`accountMessages.instantText`).
 */

const STATUSES: Readonly<Partial<Record<CustomDomain_Status, CustomDomainStatus>>> = {
	[CustomDomain_Status.PENDING]: "PENDING",
	[CustomDomain_Status.VERIFIED]: "VERIFIED",
	[CustomDomain_Status.ACTIVE]: "ACTIVE",
	[CustomDomain_Status.FAILED]: "FAILED",
	[CustomDomain_Status.DISABLED]: "DISABLED",
};

/** An unspecified certificate status has no entry: the certificate is not known, which reads as `null`. */
const CERTIFICATE_STATUSES: Readonly<Partial<Record<CustomDomain_CertificateStatus, CdnCertificateStatus>>> = {
	[CustomDomain_CertificateStatus.PENDING_VALIDATION]: "PENDING_VALIDATION",
	[CustomDomain_CertificateStatus.ISSUED]: "ISSUED",
	[CustomDomain_CertificateStatus.INACTIVE]: "INACTIVE",
	[CustomDomain_CertificateStatus.EXPIRED]: "EXPIRED",
	[CustomDomain_CertificateStatus.VALIDATION_TIMED_OUT]: "VALIDATION_TIMED_OUT",
	[CustomDomain_CertificateStatus.REVOKED]: "REVOKED",
	[CustomDomain_CertificateStatus.FAILED]: "FAILED",
	[CustomDomain_CertificateStatus.UNKNOWN]: "UNKNOWN",
};

/**
 * The domain a message carries.
 *
 * @throws Error when the status is unspecified: every section state reads the status, and a guessed one would tell the
 *               sender their domain is somewhere it is not
 */
export function customDomainOf(message: CustomDomainMessage): CustomDomain {
	const status = STATUSES[message.status];
	if (status === undefined) throw new Error("the custom domain answer carries no status");
	return {
		domain: message.domain,
		status,
		verificationRecordName: message.verificationRecordName ?? null,
		verificationRecordValue: message.verificationRecordValue ?? null,
		routingRecordName: message.routingRecordName ?? null,
		routingRecordValue: message.routingRecordValue ?? null,
		certificateStatus: CERTIFICATE_STATUSES[message.certificateStatus] ?? null,
		edgeDomainActive: message.edgeDomainActive ?? null,
		provisioningError: message.provisioningError ?? null,
		verifiedAt: message.verifiedAt === undefined ? null : instantText(message.verifiedAt),
	};
}

/**
 * The claim a message carries, or `null` for no claim or one with no label.
 *
 * A claim with no label would reach the editor as a claim and die in its render (ui#534); reading it as nothing
 * claimed is the same state a missing claim gives. `claimedAt` is carried and never read, so a claim without one is
 * still a usable claim and reads with an empty time.
 */
export function subdomainClaimOf(message: SubdomainClaimMessage | undefined): SubdomainClaim | null {
	if (message === undefined || message.label.length === 0) return null;
	return {
		label: message.label,
		hosts: message.hosts,
		claimedAt: message.claimedAt === undefined ? "" : instantText(message.claimedAt),
	};
}
